import { FieldSortKeys } from "@/base/Sorting"
import { setFieldSortDirection, setFieldSortKey } from "@/base/async-storage"
import { trackEvent } from "@/base/segment"
import { objectEntries } from "@/base/utility"
import { usePermissionCheckedAction } from "@/hooks/usePermissionCheckedAction"
import type { FieldListItemType } from "@/redux/farm-fields.reducer"
import {
  getFieldSortDirectionFromState,
  getFieldSortKeyFromState,
} from "@/redux/farm-fields.reducer"
import { useRootDispatch } from "@/redux/useRootDispatch"
import { useRootSelector } from "@/redux/useRootSelector"
import type {
  ButtonProps,
  IconButtonStyleProps,
  InvertableProps,
  MenuProps,
  RowProps,
} from "@/theme"
import {
  AppIcons,
  AppText,
  Avatar,
  Box,
  Button,
  CloseIconButton,
  ListItemTextPrimary,
  ListItemTextSecondary,
  Menu,
  Row,
  SortListButton,
  TitleWithIcon,
  testIds,
} from "@/theme"
import i18n from "@/translations/i18n"
import React from "react"
import { useTranslation } from "react-i18next"

import { logger } from "@/base/logger"
import { useRouter } from "expo-router"
import { FieldSvgComponent } from "./FieldSvg"
import { NoListItems } from "./NoListItems"

import {
  SortDirectionIconButton,
  SortListItem,
} from "./SortDirectionIconButton"

import { StyleSheet } from "react-native"

/**
 *
 */
export function CreateFieldButton({
  ...props
}: Omit<ButtonProps, "onPress">): React.JSX.Element {
  // const activeFarmId = useRootSelector(getActiveFarmIdFromState)
  const withPermissions = usePermissionCheckedAction()
  const router = useRouter()
  return (
    <Button
      IconComponent={"Draw"}
      id={"createFieldLink"}
      text={i18n.t("createField:linkTo")}
      onPress={withPermissions({
        callback: () => {
          router.navigate("/more/fields/create-field")
          trackEvent({
            // activeFarmId,
            elementName: "createFieldLink",
            name: "element_press",
          })
        },
        required: "canManageFields",
      })}
      {...props}
    />
  )
}

/**
 *
 */
export function FieldSortDirectionButton(
  props: IconButtonStyleProps & InvertableProps,
): React.JSX.Element {
  const dispatch = useRootDispatch()
  const current = useRootSelector(getFieldSortDirectionFromState)
  return (
    <SortDirectionIconButton
      currentDirection={current}
      {...props}
      onPress={() =>
        dispatch(setFieldSortDirection(current === "asc" ? "desc" : "asc"))
      }
    />
  )
}

/**
 *
 */
export function FieldsListSortMenu({
  _trigger,
  ...props
}: InvertableProps &
  Omit<MenuProps, "isOpen" | "onClose"> & {
    _trigger?: ButtonProps
  }): React.JSX.Element {
  const { t } = useTranslation("fields")
  const currentSortKey = useRootSelector(getFieldSortKeyFromState)
  const sortDirection = useRootSelector(getFieldSortDirectionFromState)
  const [isOpen, setIsOpen] = React.useState(false)
  const dispatch = useRootDispatch()
  const titleText = t("sortFields")
  const handleClose = () => setIsOpen(false)
  const items = objectEntries(FieldSortKeys)
  return (
    <React.Fragment>
      <SortListButton
        size={"sm"}
        {..._trigger}
        onPress={() => setIsOpen(true)}
      />
      <Menu
        style={styles.sortMenu}
        {...props}
        isOpen={isOpen}
        onClose={handleClose}
      >
        <Row justifyContent={"space-between"} mb={"$2"}>
          <AppText colorScheme={"secondary"}>{titleText}</AppText>
          <CloseIconButton onPress={handleClose} />
        </Row>
        {items.map(([key, { IconComponent, text }], index) => {
          const isSelected = currentSortKey === key
          /**
           *
           */
          const handlePress = () => {
            if (isSelected) {
              dispatch(
                setFieldSortDirection(
                  sortDirection === "asc" ? "desc" : "asc",
                ),
              ).catch((e) => {
                logger.error(e)
              })
            } else {
              dispatch(setFieldSortKey(key)).catch((e) => {
                logger.error(e)
              })
            }
          }

          return (
            <SortListItem
              key={key}
              IconComponent={IconComponent}
              isSelected={isSelected}
              showDivider={index < items.length - 1}
              sortDirection={sortDirection}
              text={text}
              onPress={handlePress}
            />
          )
        })}
      </Menu>
    </React.Fragment>
  )
}

export function ListEmptyComponent() {
  const { t } = useTranslation("fields")

  return (
    <NoListItems
      action={<CreateFieldButton />}
      message={t("fieldsRosterListEmpty")}
    />
  )
}

/**
 *
 */
export function ListHeaderComponent({
  HelpComponent,
  itemCount,
  ...rest
}: RowProps & {
  HelpComponent?: React.JSX.Element | null | undefined
  itemCount: number
}): React.JSX.Element {
  const { t } = useTranslation("fields")

  const titleText = t("fieldsRosterTitleWithItemCount", { itemCount })
  return (
    <Row {...rest}>
      <TitleWithIcon helpElement={HelpComponent} titleText={titleText} />
      <Row ml={"auto"}>
        <CreateFieldButton size={"sm"} />
        <Box ml={"$2"}>
          <FieldsListSortMenu />
        </Box>
        <Box ml={"$2"}>
          <FieldSortDirectionButton />
        </Box>
      </Row>
    </Row>
  )
}
const ICON_SIZE = 40

/**
 *
 */
export function RenderRow({
  areaText,
  fieldName,
  index,
  svgInfo,
}: FieldListItemType): React.JSX.Element {
  return (
    <Row
      {...testIds(`fieldsRosterItem${index}`)}
      alignItems={"center"}
      position={"relative"}
      py={"$5"}
    >
      <Avatar>
        <FieldSvgComponent
          {...svgInfo}
          fieldId={0}
          height={ICON_SIZE}
          width={ICON_SIZE}
        />
      </Avatar>
      <Box ml={"$2"}>
        <ListItemTextPrimary isTruncated>{fieldName}</ListItemTextPrimary>
        <ListItemTextSecondary isTruncated fontSize={"$xs"}>
          {areaText}
        </ListItemTextSecondary>
      </Box>
      <Box ml={"auto"}>
        <AppIcons.ListItemEnd />
      </Box>
    </Row>
  )
}
const styles = StyleSheet.create({
  sortMenu: { width: 250 },
})
